// =============================
// src/components/materials/plan/PlanOfficialForm.jsx
// =============================
import { useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import * as XLSX from "xlsx";
// import PlanSummary from "../../../components/common/materials/PlanSummary";

// 부가세율/통화 고정 (입력 필드 없음, 내부 계산용)
const TAX_RATE = 0.1; // 10%
const CURRENCY = "KRW";

// 사용자 엑셀 헤더 → 내부 필드 자동 매핑 규칙
const COLUMN_MAP = {
  code: ["품목코드", "코드", "item code", "item_code", "code", "CODE"],
  name: ["품명", "품목명", "제품명", "name", "item name", "ITEM_NAME"],
  spec: ["규격", "규격명", "스펙", "치수", "spec", "SPEC"],
  unit: ["단위", "단위명", "unit", "UNIT"],
  qty: ["수량", "수량(본)", "수량(개)", "개수", "qty", "QTY", "quantity"],
  price: [
    "단가",
    "단가(원)",
    "단가(￦)",
    "가격",
    "price",
    "unit price",
    "PRICE",
  ],
  remark: ["비고", "메모", "비고사항", "remark", "note"],
};

// 헤더 문자열 정규화 (소문자 + 공백 제거)
function normalizeHeader(str = "") {
  return str.toString().trim().toLowerCase().replace(/\s+/g, "");
}

// 엑셀 헤더 → 내부 필드 이름(code, name...) 찾기
function mapHeaderToField(header) {
  const norm = normalizeHeader(header);

  for (const field in COLUMN_MAP) {
    const candidates = COLUMN_MAP[field];
    for (const c of candidates) {
      if (norm.includes(normalizeHeader(c))) {
        return field;
      }
    }
  }
  return null;
}

// 시트 전체 rows에서 실제 헤더 행 찾기
function detectHeaderRow(rows) {
  const headerCandidates = Object.values(COLUMN_MAP)
    .flat()
    .map(normalizeHeader);

  for (let i = 0; i < rows.length; i++) {
    const rowNorm = rows[i].map(normalizeHeader);
    const hasHeaderWord = rowNorm.some((cell) =>
      headerCandidates.some((h) => cell.includes(h))
    );
    if (hasHeaderWord) return i;
  }
  return 0;
}

// 기본 컬럼 정의
const BASE_COLUMNS = [
  { key: "code", label: "품목 코드", align: "left", width: "10%" },
  { key: "name", label: "품명", align: "left", width: "16%" },
  { key: "spec", label: "규격", align: "left", width: "16%" },
  { key: "unit", label: "단위", align: "left", width: "8%" },
  { key: "qty", label: "수량", align: "right", width: "8%" },
  { key: "price", label: "단가", align: "right", width: "12%" },
  {
    key: "supply",
    label: "공급가",
    align: "right",
    width: "12%",
    readOnly: true,
  },
  { key: "vat", label: "세액", align: "right", width: "10%", readOnly: true },
  { key: "total", label: "합계", align: "right", width: "12%", readOnly: true },
];

export default function PlanOfficialForm({
  initial = {},
  projectId,
  readOnly = false,
  onChange, // (payload) => void  → FormContentManager의 getBody() 쪽에서 values로 씀
}) {
  // 초기 header 추출 (기존 데이터 호환: initial.header 또는 top-level)
  const initialHeader = initial.header || initial;

  const [title, setTitle] = useState(
    initialHeader.title || initial.title || "자재 수급 계획(공식 규격)"
  );
  const [deliveryDate, setDeliveryDate] = useState(
    initialHeader.delivery_date ||
    initial.delivery_date ||
    new Date().toISOString().slice(0, 10)
  );
  const [note, setNote] = useState(initialHeader.note || initial.note || "");

  const makeEmptyRow = () => ({
    _id: uuid(),
    code: "",
    name: "",
    spec: "",
    unit: "EA",
    qty: 0,
    price: 0,
    supply: 0,
    vat: 0,
    total: 0,
    remark: "",
  });

  const mappedInitial = (initial.items || initial.lines || []).map((r) => ({
    ...r,
    _id: r._id || uuid(),
  }));

  // lines: 기존 items 또는 lines 둘 다 지원
  const [lines, setLines] = useState(
    mappedInitial.length > 0 ? mappedInitial : [makeEmptyRow()]
  );

  const [columns, setColumns] = useState(BASE_COLUMNS);

  // initial 변경 시 동기화
  useEffect(() => {
    const h = initial.header || initial;
    setTitle(h.title || initial.title || "자재 수급 계획(공식 규격)");
    setDeliveryDate(
      h.delivery_date ||
      initial.delivery_date ||
      new Date().toISOString().slice(0, 10)
    );
    setNote(h.note || initial.note || "");
    const mapped = (initial.items || initial.lines || []).map((r) => ({
      ...r,
      _id: r._id || uuid(),
    }));
    setLines(mapped.length > 0 ? mapped : [makeEmptyRow()]);
    setColumns(BASE_COLUMNS);
  }, [JSON.stringify(initial)]);

  // 계산 행 (qty/price → 공급가/세액/합계)
  const rows = useMemo(() => {
    return (lines || []).map((r) => {
      const qty = Number(r.qty || 0) || 0;
      const price = Number(r.price || 0) || 0;
      const supply = +(qty * price).toFixed(2);
      const vat = +(supply * TAX_RATE).toFixed(2);
      const total = +(supply + vat).toFixed(2);
      return { ...r, qty, price, supply, vat, total };
    });
  }, [lines]);

  const subtotal = useMemo(
    () => rows.reduce((s, r) => s + Number(r.supply || 0), 0),
    [rows]
  );
  const vatTotal = useMemo(
    () => rows.reduce((s, r) => s + Number(r.vat || 0), 0),
    [rows]
  );
  const grandTotal = useMemo(
    () => +(subtotal + vatTotal).toFixed(2),
    [subtotal, vatTotal]
  );

  // PlanSummary에 넘길 summaryForm
  const summaryForm = useMemo(
    () => ({
      title,
      delivery_date: deliveryDate,
      note,
      items: rows.map(({ _id, ...rest }) => rest),
      subtotal,
      vat_total: vatTotal,
      total: grandTotal,
      currency: CURRENCY,
      tax_rate: TAX_RATE,
    }),
    [title, deliveryDate, note, rows, subtotal, vatTotal, grandTotal]
  );

  // 상위(FormContentManager 쪽)에 meta 전달
  useEffect(() => {
    if (!onChange) return;

    onChange({
      // FormContentManager가 values.title, values.header, values.lines를 사용
      type: "official",
      project: projectId,
      title, // top-level title도 같이 줌
      header: {
        title,
        delivery_date: deliveryDate,
        note,
        currency: CURRENCY,
        tax_rate: TAX_RATE,
      },
      lines: rows.map(({ _id, ...rest }) => rest),
      subtotal,
      vat_total: vatTotal,
      total: grandTotal,
      // summary_component: <PlanSummary form={summaryForm} />,
    });
  }, [
    title,
    deliveryDate,
    note,
    rows,
    subtotal,
    vatTotal,
    grandTotal,
    projectId,
    onChange,
    summaryForm,
  ]);

  /* ===== 행 조작 ===== */
  const addLine = () => setLines((prev) => [...prev, makeEmptyRow()]);

  const removeLine = (id) =>
    setLines((prev) => prev.filter((r) => r._id !== id));

  const updateCell = (id, key, value) =>
    setLines((prev) =>
      prev.map((r) => (r._id === id ? { ...r, [key]: value } : r))
    );

  /* ===== 엑셀에서 복붙 (Ctrl+V) ===== */
  const handlePasteToTable = (e, startRowId, startColKey) => {
    if (readOnly) return;
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    if (!text) return;

    const linesText = text
      .split(/\r?\n/)
      .map((line) => line.split("\t"))
      .filter((cells) => cells.some((c) => c !== ""));

    if (!linesText.length) return;

    const editableCols = columns.filter((c) => !c.readOnly).map((c) => c.key);

    const startColIndex = editableCols.indexOf(startColKey);
    if (startColIndex === -1) return;

    const startRowIndex = lines.findIndex((r) => r._id === startRowId);
    if (startRowIndex === -1) return;

    setLines((prev) => {
      let next = [...prev];

      const neededRows = startRowIndex + linesText.length;
      while (next.length < neededRows) {
        next = [
          ...next,
          {
            _id: uuid(),
            code: "",
            name: "",
            spec: "",
            unit: "EA",
            qty: 0,
            price: 0,
            supply: 0,
            vat: 0,
            total: 0,
            remark: "",
          },
        ];
      }

      const updated = next.map((row, rowIdx) => {
        const relRow = rowIdx - startRowIndex;
        if (relRow < 0 || relRow >= linesText.length) return row;

        const cells = linesText[relRow];
        const newRow = { ...row };

        cells.forEach((cellValue, cellIdx) => {
          const colIndex = startColIndex + cellIdx;
          if (colIndex >= editableCols.length) return;

          const key = editableCols[colIndex];
          if (key === "qty" || key === "price") {
            newRow[key] = Number(cellValue || 0);
          } else {
            newRow[key] = cellValue ?? "";
          }
        });

        return newRow;
      });

      return updated;
    });
  };

  /* ===== 엑셀 파일 업로드 ===== */
  const handleExcelUpload = (e) => {
    if (readOnly) return;
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      const data = evt.target.result;
      const workbook = XLSX.read(data, { type: "binary" });

      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      if (!worksheet || !worksheet["!ref"]) return;

      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      const rowsArr = [];
      for (let R = range.s.r; R <= range.e.r; R++) {
        const row = [];
        for (let C = range.s.c; C <= range.e.c; C++) {
          const cell = worksheet[XLSX.utils.encode_cell({ r: R, c: C })];
          row.push(cell ? cell.v : "");
        }
        rowsArr.push(row);
      }

      if (!rowsArr.length) return;

      const headerRowIndex = detectHeaderRow(rowsArr);
      const headerRow = rowsArr[headerRowIndex];
      const dataRows = rowsArr.slice(headerRowIndex + 1);

      const json = dataRows
        .filter((r) => r.some((v) => v !== "" && v !== null && v !== undefined))
        .map((r) =>
          Object.fromEntries(
            headerRow.map((h, idx) => [h || `COL${idx}`, r[idx]])
          )
        );

      if (!json.length) return;

      const excelHeaders = Object.keys(json[0]);
      const excelToFieldMap = {};
      const usedFields = new Set();

      excelHeaders.forEach((h) => {
        const field = mapHeaderToField(h);
        if (field) {
          excelToFieldMap[h] = field;
          usedFields.add(field);
        }
      });

      const hasQty = usedFields.has("qty");
      const hasPrice = usedFields.has("price");
      const hasCalc = hasQty && hasPrice;

      if (hasCalc) {
        usedFields.add("supply");
        usedFields.add("vat");
        usedFields.add("total");
      }

      let newColumns = BASE_COLUMNS.filter((col) => usedFields.has(col.key));
      if (newColumns.length === 0) {
        newColumns = BASE_COLUMNS;
      }
      setColumns(newColumns);

      const mappedLines = json.map((row) => {
        const base = {
          _id: uuid(),
          code: "",
          name: "",
          spec: "",
          unit: "EA",
          qty: 0,
          price: 0,
          supply: 0,
          vat: 0,
          total: 0,
          remark: "",
        };

        Object.entries(row).forEach(([header, value]) => {
          const field = excelToFieldMap[header];
          if (!field) return;

          if (field === "qty" || field === "price") {
            base[field] = Number(value || 0);
          } else {
            base[field] = value ?? "";
          }
        });

        return base;
      });

      if (mappedLines.length > 0) {
        setLines(mappedLines);
      }
    };

    reader.readAsBinaryString(file);
    e.target.value = "";
  };

  const hasSupplyCol = columns.some((c) => c.key === "supply");
  const hasVatCol = columns.some((c) => c.key === "vat");
  const hasTotalCol = columns.some((c) => c.key === "total");
  const showFooterTotal = hasSupplyCol && hasVatCol && hasTotalCol;

  return (
    <div className="space-y-3 min-w-0 overflow-hidden">
      {/* 헤더 */}
      <div className="space-y-1">
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">제목</label>
          <input
            className="w-full border rounded-md px-2 py-1 text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={readOnly}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">
            납기(공급)일
          </label>
          <input
            type="date"
            className="w-full border rounded-md px-2 py-1 text-sm"
            value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)}
            disabled={readOnly}
          />
        </div>
      </div>

      {/* 라인아이템 */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden min-w-0">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 min-w-0">
          <div className="font-medium text-sm min-w-0 truncate">자재 내역(공식 양식)</div>
          {!readOnly && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                className="btn-create-sm whitespace-nowrap"
                onClick={addLine}
              >
                행 추가
              </button>
              <label className="btn-basic-sm cursor-pointer whitespace-nowrap">
                엑셀 불러오기
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={handleExcelUpload}
                />
              </label>
            </div>
          )}
        </div>
        <div className="space-y-3">
          {rows.length === 0 ? (
            <div className="px-3 py-3 text-center text-gray-400 text-sm">
              행이 없습니다.
            </div>
          ) : (
            rows.map((r) => (
              <div key={r._id} className="border-2 border-gray-200 rounded-xl p-4 bg-white shadow-sm space-y-3 mb-3">
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
                  <span className="text-sm font-semibold text-gray-700">품목 정보</span>
                  {!readOnly && (
                    <button
                      className="px-3 py-1.5 rounded-md border border-red-300 text-xs font-medium whitespace-nowrap text-red-600 hover:bg-red-50 active:bg-red-100 transition-colors"
                      onClick={() => removeLine(r._id)}
                    >
                      삭제
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {columns.map((col) => {
                    const value = r[col.key] ?? "";
                    const isNumberField =
                      col.key === "qty" || col.key === "price";
                    const alignClass =
                      col.align === "right"
                        ? "text-right"
                        : col.align === "center"
                          ? "text-center"
                          : "text-left";

                    return (
                      <div key={col.key} className="space-y-1.5">
                        <label className="text-[11px] font-semibold text-gray-700 block">
                          {col.label}
                        </label>
                        {col.readOnly ? (
                          <div className={`text-sm ${alignClass} px-3 py-2 bg-gray-50 rounded-md border border-gray-200 min-h-[40px] flex items-center ${col.align === "right" ? "justify-end" : ""}`}>
                            {typeof value === "number"
                              ? value.toLocaleString()
                              : value || "-"}
                          </div>
                        ) : (
                          <input
                            type={isNumberField ? "number" : "text"}
                            className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm min-w-0 min-h-[40px] ${col.align === "right" ? "text-right" : ""
                              } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                            value={value}
                            onChange={(e) =>
                              updateCell(r._id, col.key, e.target.value)
                            }
                            onPaste={(e) =>
                              handlePasteToTable(e, r._id, col.key)
                            }
                            disabled={readOnly}
                            placeholder={col.label}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
          {showFooterTotal && rows.length > 0 && (
            <div className="border-t-2 border-gray-300 pt-2 mt-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2 text-right font-medium text-sm mb-1">합계</div>
                {columns.some((c) => c.key === "supply") && (
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">공급가</label>
                    <div className="text-xs text-right px-2 py-1.5 bg-gray-50 rounded border font-semibold">
                      {subtotal.toLocaleString()}
                    </div>
                  </div>
                )}
                {columns.some((c) => c.key === "vat") && (
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">세액</label>
                    <div className="text-xs text-right px-2 py-1.5 bg-gray-50 rounded border font-semibold">
                      {vatTotal.toLocaleString()}
                    </div>
                  </div>
                )}
                {columns.some((c) => c.key === "total") && (
                  <div className="space-y-1 col-span-2">
                    <label className="text-[10px] font-medium text-gray-500 block">합계</label>
                    <div className="text-xs text-right px-2 py-1.5 bg-gray-50 rounded border font-semibold">
                      <span>{grandTotal.toLocaleString()}</span>
                      <span className="ml-1 text-[11px] text-gray-500">
                        {CURRENCY}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 메모 */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-600">비고/메모</label>
        <textarea
          className="w-full min-h-[100px] border rounded-md px-2 py-1 text-sm"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="검수기준/납품조건 등"
          disabled={readOnly}
        />
      </div>
    </div>
  );
}
